/**
 * DEFCAT P6 — "New catalogue" dialog. Captures name / code / scope /
 * visibility / class model, POSTs via `useCreateCatalogueMutation`, and
 * navigates into the editor on success (the response carries the open
 * `draftVersionId`).
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import type {
  CatalogueScope,
  CatalogueVisibility,
  DefectCatalogueAdmin,
} from "@aeriosiq/types";
import { useCreateCatalogueMutation } from "./taxonomyQueries.js";

const CODE_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

export interface NewCatalogueDialogProps {
  open: boolean;
  /** Role-gated: offer the platform scope only to platform_admin. */
  canCreatePlatform: boolean;
  onClose: () => void;
  onCreated: (created: DefectCatalogueAdmin) => void;
}

export function NewCatalogueDialog({
  open,
  canCreatePlatform,
  onClose,
  onCreated,
}: NewCatalogueDialogProps): ReactElement {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [scope, setScope] = useState<CatalogueScope>("tenant");
  const [visibility, setVisibility] = useState<CatalogueVisibility>("private");
  const create = useCreateCatalogueMutation();

  const codeValid = code.length === 0 || CODE_RE.test(code);
  const canSubmit =
    name.trim().length > 0 && code.length > 0 && codeValid && !create.isPending;

  function reset(): void {
    setName("");
    setCode("");
    setScope("tenant");
    setVisibility("private");
    create.reset();
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  function handleSubmit(): void {
    if (!canSubmit) return;
    create.mutate(
      {
        name: { en: name.trim() },
        tenantCatalogueCode: code,
        scope,
        visibility,
      },
      {
        onSuccess: (created) => {
          reset();
          onCreated(created);
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New defect catalogue</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {create.isError ? (
            <Alert severity="error">
              Could not create the catalogue. Check the code is unique and you
              have permission for this scope.
            </Alert>
          ) : null}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            fullWidth
            required
          />
          <TextField
            label="Catalogue code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            fullWidth
            required
            error={!codeValid}
            helperText={
              codeValid
                ? "Lowercase letters, digits and hyphens (e.g. enercon-blade)."
                : "Must start with a letter/digit; lowercase, digits and hyphens only."
            }
          />
          <TextField
            select
            label="Scope"
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as CatalogueScope);
            }}
            fullWidth
            helperText={
              canCreatePlatform
                ? "Tenant catalogues need tenant_admin; platform needs platform_admin."
                : "Tenant catalogue (platform scope requires platform_admin)."
            }
          >
            <MenuItem value="tenant">Tenant</MenuItem>
            {canCreatePlatform ? (
              <MenuItem value="platform">Platform</MenuItem>
            ) : null}
          </TextField>
          <TextField
            select
            label="Visibility"
            value={visibility}
            onChange={(e) => {
              setVisibility(e.target.value as CatalogueVisibility);
            }}
            fullWidth
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="shared">Shared</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
